// Geographical API service for fetching countries, states, cities data
import { Country, State, District, City, Commune } from '@/lib/geographical-data';

// API Configuration
const REST_COUNTRIES_API = 'https://restcountries.com/v3.1';
const COUNTRY_STATE_CITY_API = 'https://api.countrystatecity.in/v1';
const CSC_API_KEY = import.meta.env.VITE_COUNTRY_STATE_CITY_API_KEY || ''; // Get from https://countrystatecity.in/

// Cache keys for localStorage
const CACHE_KEYS = {
  COUNTRIES: 'geo_countries',
  STATES: 'geo_states',
  CITIES: 'geo_cities',
  LAST_UPDATED: 'geo_last_updated',
  VERSION: 'geo_version'
};

// Cache duration (7 days in milliseconds)
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000;
const CURRENT_VERSION = '1.1';

interface CachedData<T> {
  data: T[];
  timestamp: number;
  version: string;
}

export class GeographicalApiService {
  private static instance: GeographicalApiService;
  
  public static getInstance(): GeographicalApiService {
    if (!GeographicalApiService.instance) {
      GeographicalApiService.instance = new GeographicalApiService();
    }
    return GeographicalApiService.instance;
  }

  // Check if cached data is valid
  private isCacheValid(key: string): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return false;

      const parsedCache: CachedData<any> = JSON.parse(cached);
      const now = Date.now();
      
      return (
        parsedCache.version === CURRENT_VERSION &&
        (now - parsedCache.timestamp) < CACHE_DURATION &&
        Array.isArray(parsedCache.data) &&
        parsedCache.data.length > 0
      );
    } catch (error) {
      console.warn('Cache validation error:', error);
      return false;
    }
  }

  // Get cached data
  private getCachedData<T>(key: string): T[] | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const parsedCache: CachedData<T> = JSON.parse(cached);
      return parsedCache.data;
    } catch (error) {
      console.warn('Error reading cache:', error);
      return null;
    }
  }

  // Set cached data
  private setCachedData<T>(key: string, data: T[]): void {
    try {
      const cacheData: CachedData<T> = {
        data,
        timestamp: Date.now(),
        version: CURRENT_VERSION
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error setting cache:', error);
    }
  }

  // Fetch countries from REST Countries API
  async fetchCountries(): Promise<Country[]> {
    // Check cache first
    if (this.isCacheValid(CACHE_KEYS.COUNTRIES)) {
      const cached = this.getCachedData<Country>(CACHE_KEYS.COUNTRIES);
      if (cached) {
        console.log('Using cached countries data');
        return cached;
      }
    }

    try {
      console.log('Fetching countries from API...');
      const response = await fetch(`${REST_COUNTRIES_API}/all?fields=name,cca2,idd`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const countries: Country[] = data
        .map((country: any) => ({
          code: country.cca2,
          name: country.name.common,
          dial_code: country.idd?.root ? 
            `${country.idd.root}${country.idd.suffixes?.[0] || ''}` : undefined
        }))
        .sort((a: Country, b: Country) => a.name.localeCompare(b.name));

      // Cache the data
      this.setCachedData(CACHE_KEYS.COUNTRIES, countries);
      
      console.log(`Fetched and cached ${countries.length} countries`);
      return countries;
    } catch (error) {
      console.error('Error fetching countries:', error);
      
      // Return fallback data if API fails
      return this.getFallbackCountries();
    }
  }

  // Fetch states for a specific country
  async fetchStates(countryCode: string): Promise<State[]> {
    const cacheKey = `${CACHE_KEYS.STATES}_${countryCode}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<State>(cacheKey);
      if (cached) {
        console.log(`Using cached states data for ${countryCode}`);
        return cached;
      }
    }

    try {
      console.log(`Fetching states for ${countryCode} from API...`);
      
      // For Cambodia, use our detailed local data
      if (countryCode === 'KH') {
        const cambodiaStates = this.getCambodiaStates();
        this.setCachedData(cacheKey, cambodiaStates);
        return cambodiaStates;
      }

      // Skip API call if no API key available
      if (!CSC_API_KEY) {
        console.warn('No API key provided for CountryStateCity API, using fallback data');
        throw new Error('No API key');
      }

      const response = await fetch(`${COUNTRY_STATE_CITY_API}/countries/${countryCode}/states`, {
        headers: {
          'X-CSCAPI-KEY': CSC_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const states: State[] = data
        .map((state: any) => ({
          code: state.iso2,
          name: state.name,
          country_code: countryCode
        }))
        .sort((a: State, b: State) => a.name.localeCompare(b.name));

      // Cache the data
      this.setCachedData(cacheKey, states);
      
      console.log(`Fetched and cached ${states.length} states for ${countryCode}`);
      return states;
    } catch (error) {
      console.error(`Error fetching states for ${countryCode}:`, error);
      
      // Return fallback data if API fails
      return this.getFallbackStates(countryCode);
    }
  }

  // Fetch cities for a specific state
  async fetchCities(countryCode: string, stateCode: string): Promise<City[]> {
    const cacheKey = `${CACHE_KEYS.CITIES}_${countryCode}_${stateCode}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<City>(cacheKey);
      if (cached) {
        console.log(`Using cached cities data for ${countryCode}/${stateCode}`);
        return cached;
      }
    }

    try {
      console.log(`Fetching cities for ${countryCode}/${stateCode} from API...`);
      
      // For Cambodia, use our detailed local data with districts
      if (countryCode === 'KH') {
        const cambodiaCities = this.getCambodiaCitiesAndDistricts(stateCode);
        this.setCachedData(cacheKey, cambodiaCities);
        return cambodiaCities;
      }

      // Skip API call if no API key available
      if (!CSC_API_KEY) {
        console.warn('No API key provided for CountryStateCity API, using fallback data');
        throw new Error('No API key');
      }

      const response = await fetch(`${COUNTRY_STATE_CITY_API}/countries/${countryCode}/states/${stateCode}/cities`, {
        headers: {
          'X-CSCAPI-KEY': CSC_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const cities: City[] = data
        .map((city: any) => ({
          name: city.name,
          state_code: stateCode,
          country_code: countryCode
        }))
        .sort((a: City, b: City) => a.name.localeCompare(b.name));

      // Cache the data
      this.setCachedData(cacheKey, cities);
      
      console.log(`Fetched and cached ${cities.length} cities for ${countryCode}/${stateCode}`);
      return cities;
    } catch (error) {
      console.error(`Error fetching cities for ${countryCode}/${stateCode}:`, error);
      
      // Return fallback data if API fails
      return this.getFallbackCities(countryCode, stateCode);
    }
  }

  // Get communes for Cambodia districts
  async fetchCommunes(countryCode: string, stateCode: string, districtCode: string): Promise<Commune[]> {
    // Only supported for Cambodia currently
    if (countryCode !== 'KH') return [];

    const cacheKey = `geo_communes_${countryCode}_${stateCode}_${districtCode}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Commune>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Use local Cambodia commune data
    const communes = this.getCambodiaCommunes(districtCode, stateCode);
    this.setCachedData(cacheKey, communes);
    return communes;
  }

  // Preload all geographical data (call on app startup)
  async preloadData(): Promise<void> {
    try {
      console.log('Preloading geographical data...');
      
      // Load countries
      const countries = await this.fetchCountries();
      
      // Load states for major countries
      const majorCountries = ['KH', 'US', 'CA', 'TH', 'VN', 'GB', 'AU'];
      
      for (const countryCode of majorCountries) {
        try {
          await this.fetchStates(countryCode);
          
          // For Cambodia, also preload cities/districts
          if (countryCode === 'KH') {
            const cambodiaStates = this.getCambodiaStates();
            for (const state of cambodiaStates) {
              await this.fetchCities(countryCode, state.code);
            }
          }
        } catch (error) {
          console.warn(`Failed to preload data for ${countryCode}:`, error);
        }
      }
      
      console.log('Geographical data preloading completed');
    } catch (error) {
      console.error('Error preloading geographical data:', error);
    }
  }

  // Clear all cached data (useful for testing or force refresh)
  clearCache(): void {
    try {
      Object.values(CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Also clear state and city specific caches
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('geo_states_') || key.startsWith('geo_cities_') || key.startsWith('geo_communes_'))) {
          localStorage.removeItem(key);
        }
      }
      
      console.log('Geographical cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Fallback data methods (using our current local data)
  private getFallbackCountries(): Country[] {
    return [
      { code: 'KH', name: 'Cambodia' },
      { code: 'US', name: 'United States' },
      { code: 'CA', name: 'Canada' },
      { code: 'GB', name: 'United Kingdom' },
      { code: 'AU', name: 'Australia' },
      { code: 'TH', name: 'Thailand' },
      { code: 'VN', name: 'Vietnam' },
      { code: 'LA', name: 'Laos' },
      { code: 'MM', name: 'Myanmar' },
      { code: 'SG', name: 'Singapore' },
      { code: 'MY', name: 'Malaysia' },
      { code: 'ID', name: 'Indonesia' },
      { code: 'PH', name: 'Philippines' },
      { code: 'JP', name: 'Japan' },
      { code: 'KR', name: 'South Korea' },
      { code: 'CN', name: 'China' },
      { code: 'IN', name: 'India' },
      { code: 'FR', name: 'France' },
      { code: 'DE', name: 'Germany' },
      { code: 'IT', name: 'Italy' },
      { code: 'ES', name: 'Spain' },
    ];
  }

  private getFallbackStates(countryCode: string): State[] {
    if (countryCode === 'KH') {
      return this.getCambodiaStates();
    }
    
    // Add other fallback states as needed
    const fallbackStates: { [key: string]: State[] } = {
      'US': [
        { code: 'CA', name: 'California', country_code: 'US' },
        { code: 'NY', name: 'New York', country_code: 'US' },
        { code: 'TX', name: 'Texas', country_code: 'US' },
        { code: 'FL', name: 'Florida', country_code: 'US' },
      ],
      'CA': [
        { code: 'ON', name: 'Ontario', country_code: 'CA' },
        { code: 'QC', name: 'Quebec', country_code: 'CA' },
        { code: 'BC', name: 'British Columbia', country_code: 'CA' },
      ]
    };
    
    return fallbackStates[countryCode] || [];
  }

  private getFallbackCities(countryCode: string, stateCode: string): City[] {
    if (countryCode === 'KH') {
      return this.getCambodiaCitiesAndDistricts(stateCode);
    }
    
    // Add basic fallback cities
    return [];
  }

  // Cambodia-specific data (enhanced local data)
  private getCambodiaStates(): State[] {
    return [
      { code: 'PNH', name: 'Phnom Penh', country_code: 'KH' },
      { code: 'SR', name: 'Siem Reap', country_code: 'KH' },
      { code: 'BTB', name: 'Battambang', country_code: 'KH' },
      { code: 'KPG', name: 'Kampong Cham', country_code: 'KH' },
      { code: 'KPT', name: 'Kampong Thom', country_code: 'KH' },
      { code: 'KPS', name: 'Kampong Speu', country_code: 'KH' },
      { code: 'KPC', name: 'Kampong Chhnang', country_code: 'KH' },
      { code: 'KDL', name: 'Kandal', country_code: 'KH' },
      { code: 'PY', name: 'Prey Veng', country_code: 'KH' },
      { code: 'SVG', name: 'Svay Rieng', country_code: 'KH' },
      { code: 'TKO', name: 'Takeo', country_code: 'KH' },
      { code: 'KPP', name: 'Kep', country_code: 'KH' },
      { code: 'SHV', name: 'Sihanoukville', country_code: 'KH' },
      { code: 'PST', name: 'Preah Sihanouk', country_code: 'KH' },
      { code: 'KMP', name: 'Kampot', country_code: 'KH' },
      { code: 'KKG', name: 'Koh Kong', country_code: 'KH' },
      { code: 'PVH', name: 'Pursat', country_code: 'KH' },
      { code: 'BMC', name: 'Banteay Meanchey', country_code: 'KH' },
      { code: 'ODM', name: 'Oddar Meanchey', country_code: 'KH' },
      { code: 'PLN', name: 'Pailin', country_code: 'KH' },
      { code: 'PRC', name: 'Preah Vihear', country_code: 'KH' },
      { code: 'STG', name: 'Stung Treng', country_code: 'KH' },
      { code: 'RTK', name: 'Ratanakiri', country_code: 'KH' },
      { code: 'MKL', name: 'Mondulkiri', country_code: 'KH' },
      { code: 'KRT', name: 'Kratie', country_code: 'KH' },
    ];
  }

  private getCambodiaCitiesAndDistricts(stateCode: string): City[] {
    const cambodiaDistrictData: { [key: string]: City[] } = {
      'PNH': [
        { name: 'Chamkar Mon', state_code: 'PNH', country_code: 'KH' },
        { name: 'Daun Penh', state_code: 'PNH', country_code: 'KH' },
        { name: '7 Makara', state_code: 'PNH', country_code: 'KH' },
        { name: 'Mean Chey', state_code: 'PNH', country_code: 'KH' },
        { name: 'Prampir Meakkakra', state_code: 'PNH', country_code: 'KH' },
        { name: 'Tuol Kouk', state_code: 'PNH', country_code: 'KH' },
        { name: 'Dangkao', state_code: 'PNH', country_code: 'KH' },
        { name: 'Sen Sok', state_code: 'PNH', country_code: 'KH' },
        { name: 'Russey Keo', state_code: 'PNH', country_code: 'KH' },
        { name: 'Chbar Ampov', state_code: 'PNH', country_code: 'KH' },
        { name: 'Prek Pnov', state_code: 'PNH', country_code: 'KH' },
        { name: 'Kamboul', state_code: 'PNH', country_code: 'KH' },
        { name: 'Chroy Changvar', state_code: 'PNH', country_code: 'KH' },
        { name: 'Por Sen Chey', state_code: 'PNH', country_code: 'KH' },
      ],
      'SR': [
        { name: 'Siem Reap City', state_code: 'SR', country_code: 'KH' },
        { name: 'Angkor Chum', state_code: 'SR', country_code: 'KH' },
        { name: 'Angkor Thom', state_code: 'SR', country_code: 'KH' },
        { name: 'Banteay Srei', state_code: 'SR', country_code: 'KH' },
        { name: 'Chi Kraeng', state_code: 'SR', country_code: 'KH' },
        { name: 'Krakor', state_code: 'SR', country_code: 'KH' },
        { name: 'Puok', state_code: 'SR', country_code: 'KH' },
        { name: 'Sotnikum', state_code: 'SR', country_code: 'KH' },
        { name: 'Sout Nikom', state_code: 'SR', country_code: 'KH' },
        { name: 'Svay Chek', state_code: 'SR', country_code: 'KH' },
        { name: 'Varin', state_code: 'SR', country_code: 'KH' },
      ],
      'BTB': [
        { name: 'Battambang City', state_code: 'BTB', country_code: 'KH' },
        { name: 'Banan', state_code: 'BTB', country_code: 'KH' },
        { name: 'Thmar Koul', state_code: 'BTB', country_code: 'KH' },
        { name: 'Kamrieng', state_code: 'BTB', country_code: 'KH' },
        { name: 'Koas Krala', state_code: 'BTB', country_code: 'KH' },
        { name: 'Moung Ruessei', state_code: 'BTB', country_code: 'KH' },
        { name: 'Ratanak Mondul', state_code: 'BTB', country_code: 'KH' },
        { name: 'Sangkae', state_code: 'BTB', country_code: 'KH' },
        { name: 'Samlot', state_code: 'BTB', country_code: 'KH' },
        { name: 'Sampov Lun', state_code: 'BTB', country_code: 'KH' },
        { name: 'Phnum Proek', state_code: 'BTB', country_code: 'KH' },
      ],
      'KDL': [
        { name: 'Ta Khmau', state_code: 'KDL', country_code: 'KH' },
        { name: 'Ang Snuol', state_code: 'KDL', country_code: 'KH' },
        { name: 'Kandal Stueng', state_code: 'KDL', country_code: 'KH' },
        { name: 'Khsach Kandal', state_code: 'KDL', country_code: 'KH' },
        { name: 'Leuk Daek', state_code: 'KDL', country_code: 'KH' },
        { name: 'Lvea Aem', state_code: 'KDL', country_code: 'KH' },
        { name: 'Mukh Kampul', state_code: 'KDL', country_code: 'KH' },
        { name: 'Ponhea Lueu', state_code: 'KDL', country_code: 'KH' },
        { name: 'S\'ang', state_code: 'KDL', country_code: 'KH' },
        { name: 'Saang', state_code: 'KDL', country_code: 'KH' },
        { name: 'Kien Svay', state_code: 'KDL', country_code: 'KH' },
      ]
    };
    
    return cambodiaDistrictData[stateCode] || [];
  }

  private getCambodiaCommunes(districtCode: string, stateCode: string): Commune[] {
    // Map district names to commune data
    const communeData: { [key: string]: Commune[] } = {
      'Chamkar Mon': [
        { name: 'Tonle Bassac', district_code: 'CK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Boeung Keng Kang Ti Muoy', district_code: 'CK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Boeung Keng Kang Ti Pir', district_code: 'CK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Boeung Keng Kang Ti Bei', district_code: 'CK', state_code: 'PNH', country_code: 'KH' },
      ],
      'Daun Penh': [
        { name: 'Phsar Kandal Ti Muoy', district_code: 'DK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Phsar Kandal Ti Pir', district_code: 'DK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Chaktomuk', district_code: 'DK', state_code: 'PNH', country_code: 'KH' },
        { name: 'Srah Chak', district_code: 'DK', state_code: 'PNH', country_code: 'KH' },
      ],
      'Sen Sok': [
        { name: 'Phnom Penh Thmei', district_code: 'SN', state_code: 'PNH', country_code: 'KH' },
        { name: 'Tuek Thla', district_code: 'SN', state_code: 'PNH', country_code: 'KH' },
        { name: 'Kakab', district_code: 'SN', state_code: 'PNH', country_code: 'KH' },
        { name: 'Khmuonh', district_code: 'SN', state_code: 'PNH', country_code: 'KH' },
      ],
      'Siem Reap City': [
        { name: 'Siem Reap', district_code: 'SRCITY', state_code: 'SR', country_code: 'KH' },
        { name: 'Sala Kamreuk', district_code: 'SRCITY', state_code: 'SR', country_code: 'KH' },
        { name: 'Svay Dangkum', district_code: 'SRCITY', state_code: 'SR', country_code: 'KH' },
        { name: 'Kouk Chak', district_code: 'SRCITY', state_code: 'SR', country_code: 'KH' },
      ],
      'Battambang City': [
        { name: 'Phum Kandal', district_code: 'BTBCITY', state_code: 'BTB', country_code: 'KH' },
        { name: 'Svay Por', district_code: 'BTBCITY', state_code: 'BTB', country_code: 'KH' },
        { name: 'Kampong Preah', district_code: 'BTBCITY', state_code: 'BTB', country_code: 'KH' },
        { name: 'Toul Ta Ek', district_code: 'BTBCITY', state_code: 'BTB', country_code: 'KH' },
      ],
      'Ta Khmau': [
        { name: 'Ta Khmau', district_code: 'TAKHMAU', state_code: 'KDL', country_code: 'KH' },
        { name: 'Preaek Ho', district_code: 'TAKHMAU', state_code: 'KDL', country_code: 'KH' },
        { name: 'Preaek Kampues', district_code: 'TAKHMAU', state_code: 'KDL', country_code: 'KH' },
        { name: 'Roluos', district_code: 'TAKHMAU', state_code: 'KDL', country_code: 'KH' },
      ]
    };
    
    return communeData[districtCode] || [];
  }
}

// Export singleton instance
export const geographicalApi = GeographicalApiService.getInstance();